<script setup lang="ts">
import { ref, computed, useTemplateRef, nextTick } from 'vue';
import { UiButton } from '@shopexperts/shared';
import type { AuthUser } from '@shopexperts/shared';
import LeadStepHeader from './LeadStepHeader.vue';
import SelectableSlideCard from './SelectableSlideCard.vue';
import LoginForm, { type LoginAuthResult } from './LoginForm.vue';
import RegisterForm, { type RegisterAuthResult } from './RegisterForm.vue';

interface Props {
    currentStep: number;
    totalSteps: number;
    storeName?: string;
    storeUrl?: string;
}

const props = withDefaults(defineProps<Props>(), {
    storeName: '',
    storeUrl: '',
});

const emit = defineEmits<{
    (e: 'authenticated', data: AuthenticatedData): void;
    (e: 'back'): void;
}>();

export interface AuthenticatedData {
    user: AuthUser;
    token: string;
    type: 'client' | 'expert';
    companyName?: string;
}

const authMode = ref<'login' | 'register'>('register');

const isLoginSelected = computed<boolean>({
    get: () => authMode.value === 'login',
    set: (val) => {
        if (val) authMode.value = 'login';
    },
});

const isRegisterSelected = computed<boolean>({
    get: () => authMode.value === 'register',
    set: (val) => {
        if (val) authMode.value = 'register';
    },
});

const loginFormRef = useTemplateRef<InstanceType<typeof LoginForm>>('loginFormRef');
const registerFormRef = useTemplateRef<InstanceType<typeof RegisterForm>>('registerFormRef');

const isLoading = computed(() => {
    return loginFormRef.value?.isLoading || registerFormRef.value?.isLoading;
});

const nextLabel = computed(() => {
    return authMode.value === 'login' ? 'Sign In' : 'Create Account & Submit';
});

function onNext() {
    if (authMode.value === 'login') {
        loginFormRef.value?.triggerSubmit();
    } else {
        registerFormRef.value?.triggerSubmit();
    }
}

function onLoginAuthenticated(data: LoginAuthResult) {
    emit('authenticated', {
        user: data.user,
        token: data.token,
        type: data.type,
    });
}

function onRegisterAuthenticated(data: RegisterAuthResult) {
    emit('authenticated', {
        user: data.user,
        token: data.token,
        type: 'client',
        companyName: data.companyName,
    });
}

async function switchToLogin(email: string) {
    authMode.value = 'login';
    await nextTick();
    if (email && loginFormRef.value) {
        await loginFormRef.value.setEmail(email);
        await loginFormRef.value.focusPassword();
    }
}
</script>

<template>
    <div class="flex flex-col gap-6">
        <LeadStepHeader
            caption="Account"
            :heading="authMode === 'login' ? 'Sign in to your account' : 'Create your account'"
            :subheading="authMode === 'login'
                ? 'Sign in to submit your project lead.'
                : 'Create an account to submit your project and track your leads.'"
            :current-step="currentStep"
            :total-steps="totalSteps"
        >
            <template #close-button>
                <slot name="close-button" />
            </template>
        </LeadStepHeader>

        <!-- Register card -->
        <SelectableSlideCard
            v-model="isRegisterSelected"
            title="Create an account"
            description="Sign up for a new client account."
            :allow-deselect="false"
            :expandable="true"
        >
            <RegisterForm
                ref="registerFormRef"
                :store-name="props.storeName"
                :store-url="props.storeUrl"
                @authenticated="onRegisterAuthenticated"
                @switch-to-login="switchToLogin"
            />
        </SelectableSlideCard>

        <!-- Login card -->
        <SelectableSlideCard
            v-model="isLoginSelected"
            title="I already have an account"
            description="Sign in to submit your project lead."
            :allow-deselect="false"
            :expandable="true"
        >
            <LoginForm
                ref="loginFormRef"
                @authenticated="onLoginAuthenticated"
            />
        </SelectableSlideCard>

        <!-- Shared step actions -->
        <div class="flex items-center gap-4 pt-6">
            <UiButton type="button" variant="outline" label="Back" :disabled="isLoading" @click="emit('back')" />
            <UiButton
                type="button"
                variant="primary"
                :label="nextLabel"
                class="flex-1"
                :disabled="isLoading"
                @click="onNext"
            />
        </div>
    </div>
</template>
